/**
 * Utilidades para el sistema de membresías de agentes
 */

import { collection, query, where, getDocs, updateDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Verificar y procesar trials expirados
 * Esta función debe ejecutarse periódicamente (ej: Cloud Function o cron job)
 */
export const procesarTrialsExpirados = async () => {
  try {
    const ahora = new Date();
    
    // Buscar membresías en trial que han expirado
    const membresiasQuery = query(
      collection(db, 'membresias_empresas'),
      where('estado', '==', 'en_trial')
    );
    
    const membresiasSnap = await getDocs(membresiasQuery);
    const trialsExpirados = [];
    
    membresiasSnap.forEach(doc => {
      const data = doc.data();
      const fechaFinTrial = data.fechaFinTrial?.toDate?.() || data.fechaFinTrial;
      
      if (fechaFinTrial && fechaFinTrial < ahora) {
        trialsExpirados.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    // Procesar cada trial expirado
    for (const membresia of trialsExpirados) {
      // Si no se confirmó, cancelar automáticamente
      if (!membresia.trialCompletado) {
        await updateDoc(doc(db, 'membresias_empresas', membresia.id), {
          estado: 'cancelada',
          fechaCancelacion: serverTimestamp(),
          motivoCancelacion: 'Trial expirado sin confirmación',
          fechaActualizacion: serverTimestamp()
        });
        
        // Actualizar empresa
        if (membresia.empresaId) {
          await updateDoc(doc(db, 'empresas', membresia.empresaId), {
            'membresia.estado': 'cancelada',
            'membresia.fechaCancelacion': serverTimestamp()
          });
        }
      }
    }
    
    console.log(`✅ Procesados ${trialsExpirados.length} trials expirados`);
    return { procesados: trialsExpirados.length };
    
  } catch (error) {
    console.error('❌ Error procesando trials expirados:', error);
    throw error;
  }
};

/**
 * Actualizar meses facturados de una membresía
 * Debe ejecutarse mensualmente para cada membresía activa
 */
export const actualizarMesesFacturados = async (membresiaId) => {
  try {
    const membresiaRef = doc(db, 'membresias_empresas', membresiaId);
    const membresiaSnap = await getDoc(membresiaRef);
    
    if (!membresiaSnap.exists()) {
      throw new Error('Membresía no encontrada');
    }
    
    const membresiaData = membresiaSnap.data();
    
    if (membresiaData.estado !== 'activa' && membresiaData.estado !== 'active') {
      return; // No actualizar si no está activa
    }
    
    const fechaInicio = membresiaData.fechaInicio?.toDate?.() || membresiaData.fechaInicio;
    if (!fechaInicio) {
      console.warn('Membresía sin fecha de inicio:', membresiaId);
      return;
    }
    
    const ahora = new Date();
    const mesesFacturados = calcularMesesEntreFechas(fechaInicio, ahora);
    
    // Actualizar meses facturados y comisión total
    const comisionTotal = membresiaData.comisionPorMes * mesesFacturados;
    
    await updateDoc(membresiaRef, {
      mesesFacturados,
      comisionTotal,
      ultimaFacturacion: serverTimestamp(),
      fechaActualizacion: serverTimestamp()
    });
    
    console.log(`✅ Actualizados meses facturados para membresía ${membresiaId}: ${mesesFacturados}`);
    return { mesesFacturados, comisionTotal };
    
  } catch (error) {
    console.error('❌ Error actualizando meses facturados:', error);
    throw error;
  }
};

/**
 * Calcular meses entre dos fechas
 */
export const calcularMesesEntreFechas = (fechaInicio, fechaFin) => {
  if (!fechaInicio || !fechaFin) return 0;
  
  const inicio = new Date(fechaInicio);
  const fin = new Date(fechaFin);
  
  const años = fin.getFullYear() - inicio.getFullYear();
  const meses = fin.getMonth() - inicio.getMonth();
  const dias = fin.getDate() - inicio.getDate();
  
  // Calcular meses totales aproximados
  let mesesTotales = años * 12 + meses;
  
  // Si hay más de 15 días, contar como mes adicional
  if (dias > 15) {
    mesesTotales += 1;
  } else if (dias < -15) {
    mesesTotales -= 1;
  }
  
  return Math.max(0, mesesTotales);
};

/**
 * Verificar si un trial está próximo a expirar (para notificaciones)
 */
export const obtenerTrialsPorExpirar = async (diasAntes = 1) => {
  try {
    const membresiasQuery = query(
      collection(db, 'membresias_empresas'),
      where('estado', '==', 'en_trial')
    );
    
    const membresiasSnap = await getDocs(membresiasQuery);
    const ahora = new Date();
    const fechaLimite = new Date();
    fechaLimite.setDate(fechaLimite.getDate() + diasAntes);
    
    const trials = [];
    membresiasSnap.forEach(doc => {
      const data = doc.data();
      const fechaFinTrial = data.fechaFinTrial?.toDate?.() || data.fechaFinTrial;
      
      if (fechaFinTrial && fechaFinTrial <= fechaLimite && fechaFinTrial >= ahora) {
        trials.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    return trials;
  } catch (error) {
    console.error('❌ Error obteniendo trials por expirar:', error);
    return [];
  }
};

/**
 * Formatear precio en CLP
 */
export const formatearPrecio = (precio) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP'
  }).format(precio);
};

/**
 * Formatear fecha
 */
export const formatearFecha = (fecha) => {
  if (!fecha) return 'N/A';
  const date = fecha.toDate ? fecha.toDate() : new Date(fecha);
  return date.toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

